import { Box, Button, Divider, Grid, Skeleton, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import SearchField from 'src/shared/components/form/searchField';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { useGlobals } from 'src/shared/contexts/appContext';
import { Association } from 'src/shared/models/association/association';
import { District } from 'src/shared/models/district/district';
import { AssociationAndDistrictSelect } from 'src/shared/components/organisms/associationAndDistrictSelect';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { MemberProfileGroupSearchFilter } from 'src/modules/memberProfile/models/memberProfileGroupSearchFilter';
import { MemberProfileGroupDataGrid } from 'src/modules/memberProfile/components/memberProfileGroupDataGrid';
import { ProfileGroup } from 'src/modules/member/models/profileGroup';
import { ProfileGroupRequest } from 'src/modules/memberProfile/models/profileGroupRequest';
import { ProfileGroupSection } from 'src/modules/memberProfile/components/profileGroupSection';
import { ProfileItemSection } from 'src/modules/memberProfile/components/profileItemSection';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { deleteProfileGroup, getProfileGroupById, updateProfileGroup } from 'src/shared/services/profileGroupService';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { AddCircle } from '@mui/icons-material';
import { CreateMemberProfileGroup } from 'src/modules/memberProfile/components/createMemberProfileGroup';

export const MemberRegistryProfilesPage = () => {
  const { t } = useTranslation(['memberProfile', 'common', 'fundraiser']);
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();

  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // === Lista ===
  const [association, setAssociation] = useState<Association>();
  const [district, setDistrict] = useState<District>();
  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);
  const [memberProfileGroupSearchFilter, setMemberProfileGroupSearchFilter] = useLocalStorage(LocalStorageKeys.MemberProfileGroupSearchFilter, {
    sortCol: 'title',
    sortDir: 'desc',
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
  } as MemberProfileGroupSearchFilter);

  const searchFilter: MemberProfileGroupSearchFilter = useMemo(() => {
    return {
      filter: memberProfileGroupSearchFilter?.filter,
      pageSize: memberProfileGroupSearchFilter?.pageSize,
      pageIndex: memberProfileGroupSearchFilter?.pageIndex,
      sortCol: memberProfileGroupSearchFilter?.sortCol,
      sortDir: memberProfileGroupSearchFilter?.sortDir,
      districtId: memberProfileGroupSearchFilter?.districtId,
      associationId: memberProfileGroupSearchFilter?.associationId,
      skipAccessFilter: true,
    } as MemberProfileGroupSearchFilter;
  }, [
    memberProfileGroupSearchFilter?.filter,
    memberProfileGroupSearchFilter?.pageIndex,
    memberProfileGroupSearchFilter?.pageSize,
    memberProfileGroupSearchFilter?.sortCol,
    memberProfileGroupSearchFilter?.sortDir,
    memberProfileGroupSearchFilter?.districtId,
    memberProfileGroupSearchFilter?.associationId,
  ]);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString && tempSearchString.length >= 2) {
        setMemberProfileGroupSearchFilter((prev) => ({ ...prev, filter: tempSearchString, pageIndex: 0 }));
      } else {
        setMemberProfileGroupSearchFilter((prev) => ({ ...prev, filter: '', pageIndex: 0 }));
      }
    }
  }, [isMounted, searchInitialized, setMemberProfileGroupSearchFilter, tempSearchString]);

  // === Detalj ===
  const [loading, setLoading] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(true);
  const [profileGroup, setProfileGroup] = useState<ProfileGroup>();

  const requiredMessage = t('common:validationMessages.required');
  const maxChar = 50;

  const profileItemSchema = yup.object().shape({
    memberProfileItem_id: yup.number().nullable(),
    memberProfile_id: yup.number().required(),
    name: yup
      .string()
      .max(maxChar, t('common:validationMessages.maxLength', { count: maxChar }))
      .required(requiredMessage),
    pos: yup.number().required(),
    groupName: yup.string().optional().nullable(),
    text: yup.string().optional().nullable(),
  });

  const profileGroupSchema: Schema<ProfileGroupRequest> = yup.object().shape({
    memberProfile_id: yup.number().required(requiredMessage),
    title: yup
      .string()
      .max(maxChar, t('common:validationMessages.maxLength', { count: maxChar }))
      .required(requiredMessage),
    countyAss_id: yup.number().required(requiredMessage),
    localAss_id: yup.number().required(requiredMessage),
    userFlags: yup.number().min(1, t('memberProfile:profileDetail.profileAvailableError')).required(requiredMessage),
    type: yup.number().required(requiredMessage),
    profileItems: yup
      .array()
      .of(profileItemSchema)
      .min(1, t('memberProfile:profileDetail.atLeastOneProfile'))
      .test('unique-names', t('memberProfile:profileDetail.duplicateNameError'), (items) => {
        if (!items) return true;
        const names = items.map((item) => item.name?.trim().toLowerCase());
        return new Set(names).size === names.length;
      }),
  });

  const formMethods = useForm<ProfileGroupRequest>({
    resolver: yupResolver(profileGroupSchema),
    mode: 'all',
    defaultValues: {
      memberProfile_id: 0,
      title: '',
      countyAss_id: 0,
      localAss_id: 0,
      userFlags: 0,
      type: 0,
      profileItems: [],
      deleteProfileItemsIds: [],
    },
  });

  const {
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = formMethods;

  const fetchDetail = useCallback(
    async (id: number) => {
      setLoading(true);
      setDataLoaded(true);
      try {
        const result = await getProfileGroupById(id);
        if (isMounted()) {
          const sortedData = {
            ...result.data,
            minSelectionCount: result.data.minSelectionCount ?? 0,
            maxSelectionCount: result.data.maxSelectionCount ?? 0,
            errorMessage: result.data.errorMessage ?? '',
            profileItems: result.data.profileItems?.slice().sort((a, b) => a.pos - b.pos) || [],
          };
          setProfileGroup(sortedData);
          reset(sortedData);
        }
      } catch (e) {
        ConsoleHelper.log(e);
        setDataLoaded(false);
      } finally {
        if (isMounted()) setLoading(false);
      }
    },
    [isMounted, reset],
  );

  useEffect(() => {
    if (selectedProfileId) fetchDetail(selectedProfileId);
  }, [selectedProfileId, fetchDetail]);

  const onSubmit = async (data: ProfileGroupRequest) => {
    setLoading(true);
    try {
      await updateProfileGroup({ ...data, minSelectionCount: Number(data.minSelectionCount), maxSelectionCount: Number(data.maxSelectionCount) });
      if (isMounted()) {
        toast.success(<>{t('memberProfile:profileDetail.putSuccess')}</>);
        fetchDetail(selectedProfileId!);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(<>{t('memberProfile:profileDetail.putError')}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteProfileGroup(selectedProfileId!);
      if (isMounted()) {
        toast.success(<>{t('memberProfile:profileDetail.deleteSuccess')}</>);
        setSelectedProfileId(null);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(<>{t('memberProfile:profileDetail.deleteError')}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  const syncPositions = () => {
    const items = getValues('profileItems');
    if (!items) return;
    items.forEach((_, index) => setValue(`profileItems.${index}.pos`, index + 1));
  };

  // === Visa lista eller detalj ===
  if (selectedProfileId) {
    return (
      <>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormLoaderWrapper loading={loading}>
              <Box sx={{ p: 3 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => setSelectedProfileId(null)} sx={{ mb: 2 }}>
                  {t('common:button.back')}
                </Button>
                {dataLoaded ? (
                  <>
                    <Grid spacing={3} container justifyContent='space-between'>
                      <Grid item sm={12} lg={7}>
                        <ProfileGroupSection isEditable={profileGroup?.isEditable ?? false} loading={loading} />
                      </Grid>
                      <Grid item xs='auto' sx={{ display: { xs: 'none', lg: 'block' } }}>
                        <Divider orientation='vertical' sx={{ width: '1px' }} />
                      </Grid>
                      <Grid item xs={12} sx={{ display: { xs: 'block', lg: 'none' }, my: 2 }}>
                        <Divider orientation='horizontal' sx={{ height: '1px', width: '100%' }} />
                      </Grid>
                      <Grid item xs={12} lg={4.5}>
                        <ProfileItemSection
                          isEditable={profileGroup?.isEditable ?? false}
                          memberProfileId={selectedProfileId}
                          syncPositions={() => syncPositions()}
                          loading={loading}
                        />
                        {errors.profileItems?.message && (
                          <Typography color='error' sx={{ mt: 1 }}>
                            {errors.profileItems.message}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                    <Grid item xs={12} mt={4} display='flex' justifyContent='flex-end' gap={2} alignItems='center'>
                      <Button
                        variant='outlined'
                        color='error'
                        onClick={() => setOpenDeleteDialog(true)}
                        disabled={loading || !profileGroup?.isEditable}
                      >
                        {t('common:button.delete')}
                      </Button>
                      <Button variant='contained' color='primary' type='submit' disabled={loading || !profileGroup?.isEditable}>
                        {t('common:button.save')}
                      </Button>
                    </Grid>
                  </>
                ) : (
                  <Typography variant='h5' sx={{ fontWeight: 500, color: 'text.secondary' }}>
                    {t('memberProfile:profileDetail.profileNotFound')}
                  </Typography>
                )}
              </Box>
            </FormLoaderWrapper>
          </form>
        </FormProvider>
        {openDeleteDialog && (
          <DeleteConfirmationDialog
            title={t('common:confirmDelete')}
            modaltext={
              profileGroup?.profileGroupMemberCount === 1
                ? t('memberProfile:profileDetail.profileGroupWarning', { count: profileGroup?.profileGroupMemberCount })
                : t('memberProfile:profileDetail.profileGroupWarningPlural', { count: profileGroup?.profileGroupMemberCount })
            }
            value=''
            open={openDeleteDialog}
            onClose={(val) => {
              if (val) handleDelete();
              else setOpenDeleteDialog(false);
            }}
          />
        )}
      </>
    );
  }

  return (
    <Box>
      <PageFilterSection
        leftContent={
          <SearchField
            initialValue={searchFilter.filter || ''}
            onSearch={(newSearchString) => {
              setSearchInitialized(true);
              setTempSearchString(newSearchString);
            }}
          />
        }
        rightContent={
          <Grid
            item
            xs='auto'
            sx={{
              py: 2,
              pl: 2,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
              gap: 1,
              '& .MuiAutocomplete-inputRoot': { backgroundColor: 'white' },
            }}
          >
            {currentUser ? (
              <AssociationAndDistrictSelect
                initialDistrictId={memberProfileGroupSearchFilter.districtId || undefined}
                initialAssociationId={memberProfileGroupSearchFilter.associationId || undefined}
                hideDistrict={currentUser?.districts.length === 0}
                association={association}
                district={district}
                associationCallback={(val) => {
                  setAssociation(val || undefined);
                  setMemberProfileGroupSearchFilter((prev) => ({ ...prev, associationId: val?.localAss_id }));
                }}
                districtCallback={(val) => {
                  setDistrict(val || undefined);
                  setMemberProfileGroupSearchFilter((prev) => ({ ...prev, districtId: val?.countyAss_id }));
                }}
              />
            ) : (
              <Skeleton sx={{ width: '25vw' }} />
            )}
            <Button
              onClick={() => setCreateModalOpen(true)}
              variant='contained'
              color='primary'
              startIcon={<AddCircle />}
              disableElevation
              sx={{ whiteSpace: 'nowrap' }}
            >
              {t('memberProfile:profileGroupPage.createProfileGroup')}
            </Button>
          </Grid>
        }
      />
      <MemberProfileGroupDataGrid
        loadingCallback={(x) => ConsoleHelper.log(x)}
        searchFilter={searchFilter}
        searchFilterCallback={(newFilter) => setMemberProfileGroupSearchFilter((prev) => ({ ...prev, ...newFilter }))}
        onRowClickOverride={(params) => {
          setOpenDeleteDialog(false);
          setSelectedProfileId(params.row.id);
        }}
      />
      {createModalOpen && (
        <CreateMemberProfileGroup
          modalOpen={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onCreated={(id) => {
            setCreateModalOpen(false);
            setOpenDeleteDialog(false);
            setSelectedProfileId(id);
          }}
        />
      )}
    </Box>
  );
};
